/**
 * The forecast (Lj, Nov 3: "based on project location in the project dashboard but shop location everywhere else"). The books
 * publish one doc per place (battlebooks functions/src/weather.ts, every 3 h and soon after a change):
 *   weather/now        — the shop (Settings → Shop location); with no shop set, the middle of the active jobs
 *   weather/p-{jobId}  — a job with a site of its own (its geofence, else its address)
 * Each: { loc { from, lat, lng, address, label }, site { id, name, url }, issuedAt, publishedAt, now, hours[], days[],
 * warnings[], sun { rise, set } }. Pure.
 */

// Environment Canada icon codes (weather.gc.ca/weathericons) → a Feather icon; wet = rain, snow or storms on site.
const SKY = [
  [[0, 1, 2, 32], 'sun', false], [[30, 31], 'moon', false], [[3, 10, 22, 33, 34, 35], 'cloud', false], [[20, 21, 23, 24, 44, 45], 'cloud', false],
  [[6, 28, 36], 'cloud-drizzle', true], [[11, 12, 13, 14, 15, 37], 'cloud-rain', true],
  [[7, 8, 16, 17, 18, 25, 26, 27, 38, 40], 'cloud-snow', true], [[9, 19, 39, 41, 42, 46, 47, 48], 'cloud-lightning', true], [[43], 'wind', false],
];
export function skyOf(code) {
  const c = Number(code);
  for (const [codes, icon, wet] of SKY) if (codes.includes(c)) return { icon, wet };
  return { icon: 'cloud', wet: false };
}

export const SHOP_WEATHER_ID = 'now';
export const projectWeatherId = (projectId) => `p-${projectId}`;
const STALE_MS = 12 * 3600000;   // published every 3 h: half a day old = the books aren't publishing

/** The forecast a screen shows: the job's own when it has one, else the shop's. → { doc, own } (doc null = none yet). */
export function forecastFor(docs = [], projectId = null) {
  const byId = new Map((docs || []).map((d) => [d.id, d]));
  const own = projectId ? byId.get(projectWeatherId(projectId)) : null;
  if (own && own.days?.length) return { doc: own, own: true };
  return { doc: byId.get(SHOP_WEATHER_ID) || null, own: false };
}

/** The shop's name for the forecast: "Shop", or what the jobs fall back to when no shop is set. */
export const shopPlaceLabel = (doc) => (doc?.loc?.from === 'jobs' ? 'Where the jobs are' : doc?.loc?.label || 'Shop');

/** The places with a forecast, the shop first, then each live job that has its own. → [{ key, projectId, label, icon, color, site }] */
export function forecastPlaces(docs = [], projects = []) {
  const byId = new Map((docs || []).map((d) => [d.id, d]));
  const shop = byId.get(SHOP_WEATHER_ID);
  const out = shop ? [{ key: 'shop', projectId: null, label: shopPlaceLabel(shop), icon: null, color: null, site: shop.site?.name || null }] : [];
  for (const p of projects || []) {
    if (!p || p.deletedAt || p.archived || (p.status || 'active') !== 'active') continue;
    const d = byId.get(projectWeatherId(p.id));
    if (d?.days?.length) out.push({ key: p.id, projectId: p.id, label: p.name || 'Job', icon: p.icon || null, color: p.color || null, site: d.site?.name || null });
  }
  return out;
}

const localDateOf = (ms) => { const d = new Date(ms); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

/**
 * One forecast doc → what the screens draw. `now` = ms (defaults to Date.now()), `today` = local YYYY-MM-DD.
 * → null | { site, siteUrl, place, from, address, issuedAt, publishedAt, stale, now, hours, days, warnings, sun, wetDays }
 *   hours: the next 24 from this hour; days: today on, each with { icon, wet }; warnings: the ones not yet expired.
 */
export function forecastView(doc, { now = Date.now(), today = localDateOf(now) } = {}) {
  if (!doc || !Array.isArray(doc.days)) return null;
  const t = (iso) => (iso ? Date.parse(iso) : NaN);
  const hours = (doc.hours || []).filter((h) => !(t(h.at) < now - 3600000)).slice(0, 24).map((h) => ({ ...h, ...skyOf(h.code) }));
  const days = doc.days.filter((d) => d.date >= today).map((d) => ({ ...d, ...skyOf(d.code), night: d.nightCode != null ? skyOf(d.nightCode) : null }));
  const warnings = (doc.warnings || []).filter((w) => !(t(w.expires) < now));
  const cur = doc.now ? { ...doc.now, ...skyOf(doc.now.code) } : hours[0] ? { at: hours[0].at, temp: hours[0].temp, feels: null, code: hours[0].code, condition: hours[0].condition, wind: hours[0].wind, gust: null, windDir: hours[0].windDir, humidity: null, ...skyOf(hours[0].code) } : null;
  return {
    site: doc.site?.name || null,
    siteUrl: doc.site?.url || null,
    place: doc.id === SHOP_WEATHER_ID ? shopPlaceLabel(doc) : doc.loc?.label || null,
    from: doc.loc?.from || null,
    address: doc.loc?.address || null,
    issuedAt: doc.issuedAt || null,
    publishedAt: doc.publishedAt || null,
    stale: !!doc.publishedAt && now - doc.publishedAt > STALE_MS,
    now: cur,
    hours,
    days,
    warnings,
    sun: doc.sun || null,
    wetDays: days.filter((d) => d.wet).length,
  };
}

/** Where a forecast's location came from, in plain words. */
export function forecastFromLabel(from) {
  return { shop: 'Shop location', site: 'Job site (GPS)', address: 'Job address', jobs: 'Middle of the active jobs' }[from] || null;
}
